import { cn } from "@tcg/ui";
import { useLiveQuery } from "dexie-react-hooks";
import { Banknote, Minus, Plus, ScanLine, Trash2, TriangleAlert, WifiOff } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePos, useRegister, useStaff, useAuthState } from "@/app/context";
import { MoneyText, useYen } from "@/components/MoneyText";
import { NumPad } from "@/components/NumPad";
import { useToast } from "@/components/Toast";
import { ItemBadges } from "@/features/sale/ItemBadges";
import { useScannerSettings } from "@/lib/scanner/settings";
import { useScanInput } from "@/features/sale/hooks/useScanInput";
import { findInSnapshot, readSetting, writeSetting, type SnapshotItem } from "@/lib/db";
import { bytesToBase64 } from "@/lib/printer";
import { composeOfflineReceipt } from "@/lib/printer/offlineReceipt";

import { cashSuggestions, offlineChange, offlineTotal } from "./offlineMath";
import { readTemplate } from "./OfflineRuntime";
import { enqueueSale, isOld, jstIso, nextTempReceiptNo, tempPrefix } from "./opQueue";
import { isOneOfAKind, useOfflineRules, type ItemRefusal } from "./useOfflineRules";

export const OFFLINE_CART_KEY = "offlineCart";

export interface OfflineLine {
  variantId: string;
  code: string | null;
  title: string;
  productType: string;
  rawRank: string | null;
  isConsigned: boolean;
  hasExternalListing: boolean;
  qty: number;
  unitPrice: number;
  availableAt: string | null;
}

type Phase =
  | { kind: "basket" }
  | { kind: "cash" }
  | { kind: "done"; tempNo: string; change: number; printed: boolean | null };

function titleOf(item: Pick<SnapshotItem, "title">, locale: string): string {
  return (
    (locale === "ja" ? item.title.ja : (item.title.en ?? item.title.ja)) ??
    item.title.ja ??
    item.title.en ??
    "—"
  );
}

/**
 * 販売 while offline (T07-33, design/05 §4): the tablet's own copy of the stock (Dexie snapshot)
 * decides what may be sold — sealed products always, a one-of-a-kind card only when the copy says it
 * is free, it is on no other channel and the copy is < 5 minutes old; **cash only**; the total is the
 * sum of the 税込 prices shown (the tax is computed when the sale is uploaded). 「受け取る」 queues the
 * sale in IndexedDB with its own key, prints the 仮領収書 (the server's template) and opens the
 * drawer; the real receipt number comes back with the upload.
 */
export function OfflineSaleScreen() {
  const { t, i18n } = useTranslation();
  const { db, printer } = usePos();
  const register = useRegister();
  const staff = useStaff();
  const auth = useAuthState();
  const rules = useOfflineRules();
  const toast = useToast();
  const yen = useYen();
  const locale = i18n.resolvedLanguage ?? "ja";
  const [lines, setLines] = useState<OfflineLine[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [tendered, setTendered] = useState("");
  const [phase, setPhase] = useState<Phase>({ kind: "basket" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void readSetting<OfflineLine[]>(db, OFFLINE_CART_KEY, []).then((saved) => {
      // a scan that came before the saved basket was read is kept
      setLines((current) => (current.length ? current : saved));
      setLoaded(true);
    });
  }, [db]);
  useEffect(() => {
    if (loaded) {
      void writeSetting(db, OFFLINE_CART_KEY, lines);
    }
  }, [db, lines, loaded]);

  const results = useLiveQuery(async () => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) {
      return [];
    }
    return db.snapshotItems
      .filter(
        (item) =>
          (item.title.ja ?? "").toLowerCase().includes(q) ||
          (item.title.en ?? "").toLowerCase().includes(q) ||
          (item.sku ?? "").toLowerCase() === q,
      )
      .limit(12)
      .toArray();
  }, [db, query]);
  const oldest = useLiveQuery(() => db.opQueue.orderBy("createdAt").first(), [db]);
  const meta = useLiveQuery(() => db.snapshotMeta.toCollection().last(), [db]);

  const refusal = (reason: ItemRefusal) => t(`offline.reason_${reason}`);

  const add = useCallback(
    (item: SnapshotItem, code: string | null) => {
      const verdict = rules.canSell(item);
      if (!verdict.ok) {
        setMessage(`${titleOf(item, locale)} — ${refusal(verdict.reason ?? "sold")}`);
        return;
      }
      setMessage(null);
      setLines((current) => {
        const existing = current.find((l) => l.variantId === item.variantId);
        if (existing) {
          if (isOneOfAKind(item.productType)) {
            setMessage(t("offline.already_in_basket"));
            return current;
          }
          return current.map((l) =>
            l.variantId === item.variantId
              ? { ...l, qty: Math.min(l.qty + 1, item.quantity || 99) }
              : l,
          );
        }
        return [
          ...current,
          {
            variantId: item.variantId,
            code: code ?? item.sku ?? null,
            title: titleOf(item, locale),
            productType: item.productType,
            rawRank: item.rawRank ?? null,
            isConsigned: item.isConsigned,
            hasExternalListing: item.hasExternalListing,
            qty: 1,
            unitPrice: item.price,
            availableAt: isOneOfAKind(item.productType)
              ? new Date(meta?.fetchedAt ?? item.availabilityConfirmedAt).toISOString()
              : null,
          },
        ];
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refusal() only reads t
    [locale, meta?.fetchedAt, rules, t],
  );

  const scan = useCallback(
    async (code: string) => {
      if (!loaded) {
        return;
      }
      const item = await findInSnapshot(db, code);
      if (!item) {
        setMessage(t("offline.not_in_copy", { code }));
        return;
      }
      add(item, code);
    },
    [add, db, loaded, t],
  );

  const unlocked = auth.kind === "unlocked";
  const scanner = useScannerSettings();
  const scanInput = useScanInput({
    prefix: scanner.prefix,
    terminator: scanner.suffix,
    onScan: (code) => void scan(code),
    onSearch: setQuery,
    enabled: unlocked && phase.kind !== "done" && rules.ready,
  });

  const total = useMemo(() => offlineTotal(lines), [lines]);
  const cash = Number.parseInt(tendered || "0", 10);
  const change = offlineChange(total, cash);
  const canPay = lines.length > 0 && !rules.queueFull;

  const confirm = async () => {
    if (!lines.length || cash < total || busy) {
      return;
    }
    setBusy(true);
    try {
      const regCode = register.data?.register.code ?? "R";
      const device = auth.kind === "locked" || auth.kind === "unlocked" ? auth.device : null;
      const tempNo = await nextTempReceiptNo(db, tempPrefix(regCode, device?.deviceId ?? ""));
      const at = new Date();
      await enqueueSale(db, {
        registerId: device?.registerId ?? register.data?.register.id ?? "",
        tempReceiptNo: tempNo,
        lines: lines.map((l) => ({
          variantId: l.variantId,
          code: l.code,
          qty: l.qty,
          unitPrice: l.unitPrice,
          availableAt: l.availableAt,
          hasExternalListing: l.hasExternalListing,
          title: l.title,
        })),
        total,
        tendered: cash,
        snapshotVersion: meta?.watermark ?? null,
        snapshotAt: meta ? new Date(meta.fetchedAt).toISOString() : null,
        shiftId: register.data?.openShift?.shiftId ?? null,
        staffId: staff?.id ?? null,
        staffName: staff?.name ?? null,
        locale,
        paperWidth: printer.config.paperWidth,
        at,
      });
      setLines([]);
      setTendered("");
      let printed: boolean | null = null;
      const template = await readTemplate(db, printer.config.paperWidth, locale);
      if (template) {
        const bytes = composeOfflineReceipt(template, {
          tempReceiptNo: tempNo,
          time: jstIso(at).slice(0, 16).replace("T", " "),
          staffName: staff?.name ?? null,
          lines: lines.map((l) => ({
            title: `${l.title}${l.rawRank ? ` Rank ${l.rawRank}` : ""}`,
            code: l.code,
            qty: l.qty,
            amount: l.qty * l.unitPrice,
          })),
          total,
          tendered: cash,
          change,
          yen,
        });
        const job = await printer.queue.submit({
          label: tempNo,
          receiptNo: tempNo,
          data: bytesToBase64(bytes),
          openDrawer: false,
        });
        printed = job.status === "done";
      }
      if (printer.config.drawer) {
        await printer.queue.openDrawer();
      }
      setPhase({ kind: "done", tempNo, change, printed });
    } catch (error) {
      toast.show(error instanceof Error ? error.message : t("errors.unknown"), "error");
    } finally {
      setBusy(false);
    }
  };

  if (phase.kind === "done") {
    return (
      <section className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
        <WifiOff aria-hidden className="size-12 text-pos-problem" />
        <h1 className="text-3xl font-semibold">{t("offline.done_title")}</h1>
        <p className="text-xl">
          {t("offline.done_temp_no")} <strong>{phase.tempNo}</strong>
        </p>
        <p className="text-2xl">
          {t("offline.change")} <MoneyText amount={phase.change} size="xl" />
        </p>
        <p className="text-muted-foreground">
          {phase.printed === null
            ? t("offline.no_template")
            : phase.printed
              ? t("offline.printed")
              : t("offline.print_failed")}
        </p>
        <p className="max-w-xl text-muted-foreground">{t("offline.done_note")}</p>
        <button
          type="button"
          autoFocus
          onClick={() => setPhase({ kind: "basket" })}
          className="min-h-pos-touch rounded-md bg-primary px-8 text-xl font-semibold text-primary-foreground"
        >
          {t("offline.next_customer")}
        </button>
      </section>
    );
  }

  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_auto_1fr]">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <label className="flex min-h-pos-touch flex-1 items-center gap-2 rounded-md border bg-background px-3 focus-within:ring-2 focus-within:ring-ring">
          <ScanLine className="size-6 shrink-0" aria-hidden />
          <span className="sr-only">{t("sale.scan_label")}</span>
          <input
            {...scanInput.inputProps}
            type="search"
            autoComplete="off"
            autoFocus
            disabled={!rules.ready || !loaded}
            placeholder={t("offline.scan_placeholder")}
            className="min-w-0 flex-1 bg-transparent text-xl outline-none"
          />
        </label>
      </div>
      <div
        role="status"
        className={cn(
          "flex flex-wrap items-center gap-2 border-b px-3 py-2 font-medium",
          rules.state === "offline_long" ? "bg-status-problem/15" : "bg-yellow-100",
        )}
      >
        <TriangleAlert aria-hidden className="size-5" />
        {rules.state === "offline_long" ? t("offline.banner_long") : t("offline.banner_short")}
        {rules.queueFull ? (
          <strong className="text-pos-problem">{t("offline.queue_full")}</strong>
        ) : null}
        {oldest && isOld(oldest) ? (
          <strong className="text-pos-problem">{t("offline.old_queue")}</strong>
        ) : null}
        {message ? (
          <span role="alert" className="w-full text-pos-problem">
            {message}
          </span>
        ) : null}
      </div>
      <div className="grid min-h-0 grid-cols-[minmax(0,1fr)_26rem] xl:grid-cols-[minmax(0,1fr)_30rem]">
        <section aria-label={t("offline.results")} className="min-h-0 overflow-y-auto p-3">
          {query.trim().length < 2 ? (
            <p className="text-muted-foreground">{t("offline.search_hint")}</p>
          ) : (results ?? []).length === 0 ? (
            <p className="text-muted-foreground">{t("offline.no_results")}</p>
          ) : (
            <ul className="grid grid-cols-2 gap-2 xl:grid-cols-3">
              {(results ?? []).map((item) => {
                const verdict = rules.canSell(item);
                return (
                  <li key={item.variantId}>
                    <button
                      type="button"
                      onClick={() => add(item, null)}
                      disabled={!verdict.ok}
                      aria-describedby={verdict.ok ? undefined : `why-${item.variantId}`}
                      className="flex min-h-24 w-full flex-col items-start gap-1 rounded-md border p-3 text-left disabled:opacity-60"
                    >
                      <span className="font-medium">{titleOf(item, locale)}</span>
                      <ItemBadges
                        productType={item.productType}
                        rawRank={item.rawRank ?? null}
                        consigned={item.isConsigned}
                        size="sm"
                      />
                      <MoneyText amount={item.price} />
                      {verdict.ok ? null : (
                        <span id={`why-${item.variantId}`} className="text-sm text-pos-problem">
                          {refusal(verdict.reason ?? "sold")}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        <aside aria-label={t("offline.basket")} className="flex min-h-0 flex-col border-l">
          <ul className="flex-1 overflow-y-auto">
            {lines.map((line) => (
              <li key={line.variantId} className="flex items-center gap-2 border-b px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{line.title}</p>
                  <ItemBadges
                    productType={line.productType}
                    rawRank={line.rawRank}
                    consigned={line.isConsigned}
                    size="sm"
                  />
                </div>
                {isOneOfAKind(line.productType) ? null : (
                  <span className="flex items-center gap-1">
                    <button
                      type="button"
                      aria-label={t("offline.qty_down")}
                      onClick={() =>
                        setLines((c) =>
                          c.map((l) =>
                            l.variantId === line.variantId
                              ? { ...l, qty: Math.max(1, l.qty - 1) }
                              : l,
                          ),
                        )
                      }
                      className="flex size-11 items-center justify-center rounded-md border"
                    >
                      <Minus aria-hidden className="size-5" />
                    </button>
                    <span className="w-8 text-center tabular-nums">{line.qty}</span>
                    <button
                      type="button"
                      aria-label={t("offline.qty_up")}
                      onClick={() =>
                        setLines((c) =>
                          c.map((l) =>
                            l.variantId === line.variantId ? { ...l, qty: l.qty + 1 } : l,
                          ),
                        )
                      }
                      className="flex size-11 items-center justify-center rounded-md border"
                    >
                      <Plus aria-hidden className="size-5" />
                    </button>
                  </span>
                )}
                <MoneyText amount={line.unitPrice} />
                <button
                  type="button"
                  aria-label={t("offline.remove", { title: line.title })}
                  onClick={() => setLines((c) => c.filter((l) => l.variantId !== line.variantId))}
                  className="flex size-11 items-center justify-center rounded-md hover:bg-accent"
                >
                  <Trash2 aria-hidden className="size-5" />
                </button>
              </li>
            ))}
            {lines.length === 0 ? (
              <li className="p-4 text-muted-foreground">{t("offline.basket_empty")}</li>
            ) : null}
          </ul>
          <div className="border-t p-3">
            <p className="flex items-baseline justify-between text-xl font-semibold">
              {t("offline.total")} <MoneyText amount={total} size="xl" />
            </p>
            <p className="text-sm text-muted-foreground">{t("offline.tax_note")}</p>
            {phase.kind === "cash" ? (
              <div className="mt-2 flex flex-col gap-2">
                <div className="flex flex-wrap gap-2">
                  {cashSuggestions(total).map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setTendered(String(value))}
                      className="min-h-12 rounded-md border px-3 text-lg tabular-nums"
                    >
                      {value === total ? t("offline.exact") : yen(value)}
                    </button>
                  ))}
                </div>
                <NumPad
                  label={t("offline.tendered")}
                  value={tendered}
                  onChange={setTendered}
                  money
                  onEnter={() => void confirm()}
                  enterLabel={t("offline.confirm")}
                  disabled={busy}
                />
                <p className="flex justify-between text-lg">
                  {t("offline.change")}
                  {cash >= total ? (
                    <MoneyText amount={change} size="lg" />
                  ) : (
                    <span>{t("offline.not_enough")}</span>
                  )}
                </p>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setTendered(String(total));
                    setPhase({ kind: "cash" });
                  }}
                  disabled={!canPay}
                  className="mt-2 flex min-h-pos-touch w-full items-center justify-center gap-2 rounded-md bg-primary text-xl font-semibold text-primary-foreground disabled:opacity-50"
                >
                  <Banknote aria-hidden className="size-6" />
                  {t("offline.pay_cash")}
                </button>
                <p className="mt-1 text-sm text-muted-foreground">{t("offline.cash_only")}</p>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
